import { maybeAskForTrustpilotReview } from 'components/organisms/TrustpilotReview';
import _get from 'lodash/get';
import { useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router';
import { UserCacheKeys } from 'types/cacheKeys.types';
import type { LoginData } from 'types/login.types';
import { signupDateStorageKey } from 'utils/constants';

const useGetOnUserLogin = (target?: string) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const fromLocation = _get(location, 'state.from', {});
  const loginCount = localStorage.getItem('loginCount');
  return (
    { data: user, clientId, ...authData }: LoginData,
    innerTarget?: string,
  ) => {
    const currentClientId = localStorage.getItem('clientId');
    const lastLoggedInUserId = localStorage.getItem('lastLoggedInUserId');
    const startedAsNotSelfEmployed = localStorage.getItem(
      'startedAsNotSelfEmployed',
    );
    const startedAsRASORegister = localStorage.getItem('startedAsRASORegister');
    const newLoginCount = Number(loginCount || 0) + 1;
    localStorage.setItem('loginCount', String(newLoginCount));

    if (newLoginCount >= 10) {
      maybeAskForTrustpilotReview();
    }

    if (clientId && !currentClientId) {
      localStorage.setItem('clientId', clientId);
    }

    if (!startedAsNotSelfEmployed) {
      localStorage.setItem(
        'startedAsNotSelfEmployed',
        String(!!user?.isNotYetSelfEmployed),
      );
    }

    if (!startedAsRASORegister) {
      localStorage.setItem(
        'startedAsRASORegister',
        String(!!user?.raso?.referenceId),
      );
    }

    if (user.created) {
      localStorage.setItem(signupDateStorageKey, user.created);
    }

    // Initially set the user data query state with the data received from the login response (only for the first time)
    // Then, the user data query will fetch the user data from the server on subsequent reloads
    queryClient.setQueryData([UserCacheKeys.UserData], user);

    authData.exp = authData.exp - authData.iat + Date.now() / 1000;

    localStorage.setItem('auth', JSON.stringify(authData));
    localStorage.setItem('lastLoggedInUserId', user?._id);

    // Prevent redirection to previous/targeted pages unless it's a new user
    // or, it's the same user that logged-in last session to avoid conflicts
    const hasPreviousRedirectURL =
      !innerTarget && !target && fromLocation?.pathname;

    const isTheSameUser =
      !lastLoggedInUserId || lastLoggedInUserId === user?._id;

    if (hasPreviousRedirectURL && isTheSameUser) {
      const redirectionURL = `${fromLocation?.pathname}${
        fromLocation?.search || ''
      }`;

      return navigate(redirectionURL);
    }

    navigate(innerTarget || target || '/');
  };
};

export default useGetOnUserLogin;
